import { useAuth } from "@clerk/clerk-react";
import { useEffect, useState } from "react";
import { useApiFetch } from "../hooks/useApi";

type TenantRow = { id: string; name: string; wildix_signing_configured?: boolean };

export function TenantsPage() {
  const { isLoaded, isSignedIn } = useAuth();
  const api = useApiFetch();
  const [state, setState] = useState<
    | { status: "idle" }
    | { status: "loading" }
    | { status: "ok"; tenants: TenantRow[] }
    | { status: "err"; message: string; statusCode?: number }
  >({ status: "idle" });

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setState({ status: "loading" });
        const res = await api("/api/v1/tenants");
        const text = await res.text();
        if (cancelled) return;
        if (!res.ok) {
          let detail = text;
          try {
            const j = JSON.parse(text) as { error?: string; reason?: string; detail?: string };
            detail = j.reason ?? j.error ?? j.detail ?? text;
          } catch {
            /* raw text */
          }
          setState({
            status: "err",
            message: detail,
            statusCode: res.status,
          });
          return;
        }
        const data = JSON.parse(text) as { tenants?: TenantRow[] };
        setState({ status: "ok", tenants: data.tenants ?? [] });
      } catch (e) {
        if (!cancelled) {
          setState({ status: "err", message: e instanceof Error ? e.message : String(e) });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, isLoaded, isSignedIn]);

  return (
    <div>
      <p className="sd-kicker">Central metadata</p>
      <h1 className="sd-h1">Tenants</h1>
      <p className="sd-lead">
        Registry from <code>GET /api/v1/tenants</code> — requires <strong>Platform Admin</strong> domain (
        <code>PLATFORM_ADMIN_EMAIL_ALLOWLIST</code>).
      </p>

      {state.status === "loading" || state.status === "idle" ? (
        <div className="sd-main-card" style={{ color: "var(--sd-muted)" }}>
          Loading…
        </div>
      ) : null}

      {state.status === "err" ? (
        <div
          className="sd-main-card"
          style={{
            borderColor: state.statusCode === 403 ? "var(--sd-border)" : "rgba(248,113,113,0.35)",
          }}
        >
          <strong style={{ color: state.statusCode === 403 ? "var(--sd-muted)" : "var(--sd-danger)" }}>
            {state.statusCode === 403 ? "Access restricted" : "Request failed"}
          </strong>
          {state.statusCode === 403 ? (
            <p style={{ margin: "0.75rem 0 0", color: "var(--sd-muted)", fontSize: "0.9rem" }}>
              Your account is signed in, but this route is limited to Platform Admins (email domain on the allowlist).
              This is expected for partner/customer test users.
            </p>
          ) : (
            <p style={{ margin: "0.75rem 0 0", fontSize: "0.85rem", fontFamily: "var(--sd-mono)" }}>{state.message}</p>
          )}
        </div>
      ) : null}

      {state.status === "ok" ? (
        <div className="sd-main-card" style={{ padding: 0, overflow: "hidden" }}>
          {state.tenants.length === 0 ? (
            <div style={{ padding: "1.25rem", color: "var(--sd-muted)" }}>
              No tenants in D1 yet — seed <code>tenant_registry</code> or run migrations.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "1px solid var(--sd-border)", color: "var(--sd-muted)" }}>
                  <th style={{ padding: "0.75rem 1.25rem", fontWeight: 600 }}>ID</th>
                  <th style={{ padding: "0.75rem 1.25rem", fontWeight: 600 }}>Name</th>
                  <th style={{ padding: "0.75rem 1.25rem", fontWeight: 600 }}>Wildix signing</th>
                </tr>
              </thead>
              <tbody>
                {state.tenants.map((t) => (
                  <tr key={t.id} style={{ borderBottom: "1px solid var(--sd-border)" }}>
                    <td style={{ padding: "0.65rem 1.25rem", fontFamily: "var(--sd-mono)", fontSize: "0.8rem" }}>{t.id}</td>
                    <td style={{ padding: "0.65rem 1.25rem" }}>{t.name}</td>
                    <td style={{ padding: "0.65rem 1.25rem", fontSize: "0.85rem" }}>
                      {t.wildix_signing_configured ? "configured" : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}
    </div>
  );
}
